import { gameEnDictionary } from "./localization/game.en";
import { createGameAudioOverrides } from "./audio/createGameAudioOverrides";
import { INTRO_SPLASH_SPEC } from "./info/IntroSplashSpec";
import { buildPaytableTokens } from "./info/PaytableSpec";
import { buildFeaturePurchaseInfoTokens } from "./info/FeaturePurchaseInfoTokens";
import { createFeaturePurchaseOffers } from "./feature/createFeaturePurchaseOffers";
import { createFixtureCatalog } from "./sim/fixtures";
import { HISTORY_FIXTURE } from "./sim/historyFixture";
import { baseFixtureReelTumbleChain } from "./sim/fixtures/base/base_fixture_reel_tumble_chain";
import { fsFixtureFreeSpins } from "./sim/fixtures/feature/fs_fixture_free_spins";
import { BOOT_FALLBACK_GRID } from "./composition/bootFallbackGrid";
import { SPEED_MODE_SCALES } from "./tuning/speedModeTuning";
import { createScene, type SlotCannonScene } from "./scene/createScene";
import { createPresentation } from "./composition/createPresentation";
import { SELECTABLE_FIXTURES } from "./sim/fixtureRegistry";
import { buildFilterTestScene } from "./dev/filterTestScene";
import { installSlotCannonHook } from "./dev/installSlotCannonHook";
import { version } from "../package.json";
import { FixtureGameHistoryProvider, FixtureReplayRoundProvider, FixtureRoundProvider, loadLocalizationDictionaryFromBundle, type GameDefinition } from "@slot/game-shell";

/** The package's own version (games/slot-cannon/package.json), so a release bumps one number. */
const GAME_VERSION = version;

/**
 * slot-cannon as one object (plan §2.2): what the shell's boot, chrome and offline data take from this
 * title, and the factories for its scene and its round presentation.
 */
export const slotCannon: GameDefinition<SlotCannonScene> = {
  id: "slot-cannon",
  version: GAME_VERSION,
  grid: { columns: 5, rows: 4 },
  speedModeScales: SPEED_MODE_SCALES,
  bootFallbackGrid: BOOT_FALLBACK_GRID,

  boot: {
    locale: {
      baseDictionary: gameEnDictionary,
      loadRemoteDictionary: ({ locale, gameBundleRegistry }) => loadLocalizationDictionaryFromBundle(gameBundleRegistry, `localization/game.${locale}`),
    },
    /** Offline fixture pricing for the game's feature ids + the big-win tier table. */
    fixtureSettingsOverrides: {
      featurePriceMultipliersById: {
        super_bonus_buy: 100,
      },
      featureModeMultipliersById: {
        bonus_hunt_mode: 1.5,
      },
      bigWinTiers: [
        { levelIdentifier: "nice", minWinMultiplier: 8 },
        { levelIdentifier: "big", minWinMultiplier: 15 },
        { levelIdentifier: "huge", minWinMultiplier: 40 },
        { levelIdentifier: "epic", minWinMultiplier: 100 },
      ],
    },
    // The shell loads its core set (`shared-core`, `game-shared-visuals`, `shell-ui`) itself; this title has no bundle of its own.
    extraBundles: [],
    audioOverrides: createGameAudioOverrides(),
  },

  chrome: {
    introSplash: INTRO_SPLASH_SPEC,
    // The game's info-page tokens.
    buildExtraInfoTokens: (input) => ({
      ...buildPaytableTokens(input),
      ...buildFeaturePurchaseInfoTokens({
        featurePriceMultipliersById: input.initialSettings.featurePriceMultipliersById ?? {},
        featureModeMultipliersById: input.initialSettings.featureModeMultipliersById ?? {},
      }),
    }),
    featurePurchaseOffers: createFeaturePurchaseOffers(),
  },

  fixtures: {
    createCatalog: () => createFixtureCatalog(),
    // Offline dev + the e2e suite: the fixture round robin, or a playlist of the selectable fixtures (the dev menu, the hook).
    createRoundProvider: () =>
      new FixtureRoundProvider(createFixtureCatalog(), SELECTABLE_FIXTURES, {
        simulatedLatencyMs: 50,
        cycleThroughFixtures: true,
        startGroup: "base",
        rejectLowBalanceForTesting: false,
        rejectedBalanceAmount: "420",
        rejectedCurrencyCode: "AMD",
      }),
    createHistoryProvider: () => new FixtureGameHistoryProvider(HISTORY_FIXTURE, { simulatedLatencyMs: 300 }),
    // `?replay=` / `?pending=`: the free spins for the history's biggest win, the tumble chain for any other id, the shell's `fail` for the failure path.
    createReplayRoundProvider: () =>
      new FixtureReplayRoundProvider({ roundsById: { "4015571829392": fsFixtureFreeSpins }, defaultRound: baseFixtureReelTumbleChain }, { simulatedLatencyMs: 300 }),
  },

  createScene,
  createPresentation,
  dev: {
    installHook: installSlotCannonHook,
    createFilterTestScene: buildFilterTestScene,
  },
};
